import { useContext, useEffect, useState } from "react";
import { RiBankLine } from "react-icons/ri";

import HeadlessDialog from "@components/Dialog";
import HeadlessButton from "@components/Button";
import ScrollableModalBody from "@components/ScrollableModalBody";
import Spinner from "@components/Spinner";
import axiosClient from "@service/axiosClient";
import { updateContractor } from "@service/contractorService";
import { IWorkerInfoDTO } from "@interfaces/IWorkerInfoDTO";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";

interface WorkerBankInfoModalProps {
  workerId: number;
  contractorName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function WorkerBankInfoModal({
  workerId,
  contractorName,
  isOpen,
  onClose,
  onSaved,
}: WorkerBankInfoModalProps): JSX.Element | null {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [bankAccount, setBankAccount] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [legacyAchId, setLegacyAchId] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !workerId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await axiosClient.get<IWorkerInfoDTO>(
          `/worker_api/info/get/${workerId}`
        );
        if (cancelled) return;
        setBankAccount(res.data.bank_account || "");
        setRoutingNumber((res.data as any).routing_number || "");
        setLegacyAchId((res.data as any).legacy_ach_id || "");
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.error || "Failed to load worker bank info."
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, workerId]);

  const handleSave = async () => {
    setError(null);
    if (routingNumber && !/^\d{9}$/.test(routingNumber)) {
      setError("Routing number must be exactly 9 digits.");
      return;
    }
    if (bankAccount && !/^\d+$/.test(bankAccount)) {
      setError("Account number must contain only digits.");
      return;
    }
    setIsSaving(true);
    try {
      await updateContractor(workerId, {
        bank_account: bankAccount,
        routing_number: routingNumber,
        legacy_ach_id: legacyAchId,
      } as any);
      triggerSnackbar("Bank info updated successfully.");
      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update bank info.";
      setError(msg);
      triggerSnackbar(`Bank info update failed: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <RiBankLine className="tw-text-primary-light" size={24} />
          <span>{`Bank Info - ${contractorName}`}</span>
        </div>
      }
      description="Edit the worker's bank account and routing number."
      isOpen={isOpen}
      setIsOpen={(open: boolean) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      buttonText="Close"
      size="tw-w-[100vw] xl:tw-max-w-[40vw]"
    >
      {isLoading ? (
        <div className="tw-p-6">
          <Spinner className="tw-flex tw-justify-center tw-items-center" />
        </div>
      ) : (
        <ScrollableModalBody className="tw-overflow-y-auto">
          <div className="tw-space-y-4 tw-ml-[2px]">
            {error && (
              <div className="tw-p-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-700 tw-text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Bank Account Number
              </label>
              <input
                type="text"
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value.trim())}
                placeholder="Enter account number"
                inputMode="numeric"
              />
            </div>

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Routing Number
              </label>
              <input
                type="text"
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                value={routingNumber}
                onChange={(e) => setRoutingNumber(e.target.value.trim())}
                placeholder="Enter 9-digit routing number"
                inputMode="numeric"
                maxLength={9}
              />
            </div>

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Legacy ACH ID
              </label>
              <input
                type="text"
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                value={legacyAchId}
                onChange={(e) => setLegacyAchId(e.target.value)}
                placeholder="Enter legacy ACH ID"
                maxLength={255}
              />
            </div>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-200">
              <HeadlessButton
                type="button"
                disabled={isSaving}
                onClick={handleSave}
                className="md:tw-w-[30%] tw-h-10"
              >
                {isSaving ? (
                  <Spinner className="tw-flex tw-justify-center tw-items-center" />
                ) : (
                  "Save"
                )}
              </HeadlessButton>
            </div>
          </div>
        </ScrollableModalBody>
      )}
    </HeadlessDialog>
  );
}
